import "./styles.css";
import { useAgent } from "agents/react";
import { Component, StrictMode, useEffect, useMemo, useRef, useState, type ErrorInfo, type FormEvent, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import type { HankAgent } from "./hank-agent";
import type { DashboardData, HankState, ProfileInput, SearchProfile } from "./types";

type AuthStatus = {
  configured: boolean;
  authenticated: boolean;
  passwordSalt?: string;
  passwordIterations?: number;
};
type Tab = "home" | "watches" | "hank" | "settings";

const initialHankState: HankState = {
  status: "idle",
  message: "Connecting to Hank…",
  activeProfileId: null,
  lastRunAt: null,
  lastError: null,
};

function RootApp() {
  const [auth, setAuth] = useState<AuthStatus | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    void fetchJson<AuthStatus>("/api/auth/status")
      .then(setAuth)
      .catch((reason: unknown) => setError(errorMessage(reason)));
  }, []);

  if (error) return <CenteredMessage title="Hank could not start" body={error} />;
  if (!auth) return <Splash />;
  if (!auth.authenticated) {
    return (
      <AuthScreen
        configured={auth.configured}
        passwordSalt={auth.passwordSalt ?? null}
        passwordIterations={auth.passwordIterations ?? 120_000}
        onAuthChange={setAuth}
      />
    );
  }
  return <AuthenticatedApp onLogout={() => setAuth({ configured: true, authenticated: false })} />;
}

class AppErrorBoundary extends Component<{ children: ReactNode }, { message: string }> {
  state = { message: "" };

  static getDerivedStateFromError(error: unknown): { message: string } {
    return { message: errorMessage(error) || "The app hit an unexpected problem." };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error("Hank client render failed", error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.message) return this.props.children;
    return (
      <div className="auth-screen">
        <section className="auth-card">
          <div className="auth-logo">H</div>
          <h1>Hank stayed online</h1>
          <p>The screen hit a display problem, but your watches and settings are safe.</p>
          <button className="primary-button recovery-button" type="button" onClick={() => window.location.reload()}>
            Reload Hank
          </button>
          <small>{this.state.message}</small>
        </section>
      </div>
    );
  }
}

function AuthenticatedApp({ onLogout }: { onLogout: () => void }) {
  const [hankState, setHankState] = useState(initialHankState);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [tab, setTab] = useState<Tab>("home");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [showProfile, setShowProfile] = useState(false);
  const [editing, setEditing] = useState<SearchProfile | null>(null);

  const agent = useAgent<HankAgent, HankState>({
    agent: "HankAgent",
    name: "owner",
    onStateUpdate: setHankState,
    onError: (event) => setNotice(event instanceof ErrorEvent ? event.message : "Hank’s live connection was interrupted."),
  });

  const refresh = async () => {
    setBusy(true);
    try {
      setDashboard(await agent.stub.dashboard());
    } catch (reason) {
      setNotice(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void agent.ready.then(refresh).catch((reason: unknown) => setNotice(errorMessage(reason)));
  }, [agent.ready]);

  const handleDashboard = (next: DashboardData) => {
    setDashboard(next);
    setShowProfile(false);
    setEditing(null);
  };

  const runAll = async () => {
    setBusy(true);
    setNotice("");
    try {
      const result = await agent.stub.runAllNow();
      setDashboard(result.dashboard);
    } catch (reason) {
      setNotice(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await fetchJson("/api/auth/logout", { method: "POST" });
    onLogout();
  };

  return (
    <div className="app-shell">
      <Header status={hankState.status} onRefresh={() => void refresh()} busy={busy} />
      {notice && (
        <button className="notice" onClick={() => setNotice("")} type="button">
          <span>{notice}</span><span aria-hidden="true">×</span>
        </button>
      )}
      <main>
        {tab === "home" && (
          <DashboardView
            dashboard={dashboard}
            hankState={hankState}
            busy={busy}
            onRunAll={() => void runAll()}
            onAdd={() => { setEditing(null); setShowProfile(true); }}
            onOpenHank={() => setTab("hank")}
          />
        )}
        {tab === "watches" && (
          <WatchesView
            dashboard={dashboard}
            busy={busy}
            onAdd={() => { setEditing(null); setShowProfile(true); }}
            onEdit={(profile) => { setEditing(profile); setShowProfile(true); }}
            onRun={async (id) => {
              setBusy(true);
              try {
                const result = await agent.stub.runProfileNow(id);
                setDashboard(result.dashboard);
              } catch (reason) {
                setNotice(errorMessage(reason));
              } finally {
                setBusy(false);
              }
            }}
            onToggle={async (id, active) => handleDashboard(await agent.stub.setSearchProfileActive(id, active))}
            onRemove={async (id) => {
              if (window.confirm("Remove this travel watch and its recorded offer history?")) {
                handleDashboard(await agent.stub.removeSearchProfile(id));
              }
            }}
          />
        )}
        {tab === "hank" && (
          <HankView
            state={hankState}
            dashboard={dashboard}
            busy={busy}
            onSend={async (message) => {
              setBusy(true);
              try {
                const result = await agent.stub.chat(message);
                if (result.dashboard) setDashboard(result.dashboard);
                else await refresh();
                return result.reply;
              } finally {
                setBusy(false);
              }
            }}
          />
        )}
        {tab === "settings" && <SettingsView dashboard={dashboard} onLogout={() => void logout()} />}
      </main>
      <BottomNav tab={tab} onChange={setTab} />
      {showProfile && (
        <ProfileSheet
          existing={editing}
          onClose={() => { setShowProfile(false); setEditing(null); }}
          onSave={async (input) => {
            setBusy(true);
            try {
              const next = editing
                ? await agent.stub.updateSearchProfile(editing.id, input)
                : await agent.stub.createSearchProfile(input);
              handleDashboard(next);
            } finally {
              setBusy(false);
            }
          }}
        />
      )}
    </div>
  );
}

function Header({ status, onRefresh, busy }: { status: HankState["status"]; onRefresh: () => void; busy: boolean }) {
  return (
    <header className="topbar">
      <div className="brand-lockup">
        <div className="brand-mark">H</div>
        <div><strong>Hank</strong><span>travel intelligence</span></div>
      </div>
      <button className="icon-button" type="button" onClick={onRefresh} disabled={busy} aria-label="Refresh dashboard">
        <span className={busy ? "spin" : ""}>↻</span>
        <i className={`presence ${status}`} />
      </button>
    </header>
  );
}

function DashboardView({ dashboard, hankState, busy, onRunAll, onAdd, onOpenHank }: {
  dashboard: DashboardData | null;
  hankState: HankState;
  busy: boolean;
  onRunAll: () => void;
  onAdd: () => void;
  onOpenHank: () => void;
}) {
  if (!dashboard) return <DashboardSkeleton />;
  const providerMissing = !dashboard.providerStatus.flights;
  return (
    <div className="page dashboard-page">
      <section className={`hank-brief ${hankState.status}`}>
        <div className="eyebrow"><span className="status-dot" /> Hank’s brief</div>
        <h1>{providerMissing ? "I’m ready. Connect the live search source." : hankState.message}</h1>
        {providerMissing && <p>Your app and schedules are live. Add the LetsFG token to start collecting real fares.</p>}
        {!providerMissing && hankState.message !== "" && <p>{hankState.message}</p>}
        <div className="brief-actions">
          <button className="primary-button" type="button" onClick={onRunAll} disabled={busy || providerMissing}>
            {busy ? "Hank is working…" : "Run every watch"}
          </button>
          <button className="ghost-button" type="button" onClick={onOpenHank}>Talk to Hank</button>
        </div>
      </section>

      <section className="stat-grid" aria-label="Monitoring summary">
        <Stat label="Active watches" value={dashboard.stats.activeProfiles} />
        <Stat label="Deal signals" value={dashboard.stats.dealsFound} accent />
        <Stat label="7-day checks" value={dashboard.stats.searchesThisWeek} />
        <Stat label="Best drop" value={dashboard.stats.bestDropPct === null ? "—" : `${dashboard.stats.bestDropPct.toFixed(0)}%`} />
      </section>

      <SectionHeading title="Strongest deals" action="All watches" onAction={onAdd} />
      {dashboard.deals.length === 0 ? (
        <EmptyState
          title="No price history yet"
          body={providerMissing ? "Create a watch now, then connect LetsFG to start the first live sweep." : "Create a watch and let Hank establish a price baseline."}
          action="Create a travel watch"
          onAction={onAdd}
        />
      ) : (
        <div className="deal-stack">
          {dashboard.deals.slice(0, 5).map((deal) => <DealCard key={deal.id} deal={deal} />)}
        </div>
      )}

      <SectionHeading title="Recent checks" />
      <div className="timeline-card">
        {dashboard.recentRuns.length === 0 ? <p className="muted">Hank has not run a check yet.</p> : dashboard.recentRuns.slice(0, 5).map((run) => (
          <div className="timeline-row" key={run.id}>
            <span className={`run-status ${run.status}`} />
            <div><strong>{run.profileName ?? "Travel sweep"}</strong><span>{run.hankSummary ?? run.errorMessage ?? `${run.offerCount} offers recorded`}</span></div>
            <time>{formatRelative(run.startedAt)}</time>
          </div>
        ))}
      </div>
    </div>
  );
}

function WatchesView({ dashboard, busy, onAdd, onEdit, onRun, onToggle, onRemove }: {
  dashboard: DashboardData | null;
  busy: boolean;
  onAdd: () => void;
  onEdit: (profile: SearchProfile) => void;
  onRun: (id: string) => Promise<void>;
  onToggle: (id: string, active: boolean) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
}) {
  return (
    <div className="page">
      <div className="page-title"><div><span className="eyebrow">Monitoring plan</span><h1>Travel watches</h1></div><button className="primary-button compact" type="button" onClick={onAdd}>New watch</button></div>
      {!dashboard ? <DashboardSkeleton /> : dashboard.profiles.length === 0 ? (
        <EmptyState title="Tell Hank what to watch" body="Use an exact route with a flexible departure window. Nearby airports can be separate watches." action="Create your first watch" onAction={onAdd} />
      ) : (
        <div className="watch-list">
          {dashboard.profiles.map((profile) => (
            <article className={`watch-card ${profile.active ? "" : "paused"}`} key={profile.id}>
              <div className="watch-head"><div><span className="eyebrow">{profile.name}</span><h2>{profile.origin}<span>→</span>{profile.destination}</h2></div><button className={`switch ${profile.active ? "on" : ""}`} aria-label={profile.active ? "Pause watch" : "Activate watch"} type="button" onClick={() => void onToggle(profile.id, !profile.active)}><i /></button></div>
              <div className="watch-meta"><span>{formatDate(profile.departureStart)}–{formatDate(profile.departureEnd)}</span><span>{profile.tripLengthDays} nights</span><span>{profile.maxStops === 0 ? "Nonstop" : `≤ ${profile.maxStops} stop${profile.maxStops === 1 ? "" : "s"}`}</span></div>
              <div className="watch-budgets"><div><span>Trip target</span><strong>{profile.targetTotal === null ? "Open" : money(profile.targetTotal, profile.currency)}</strong></div><div><span>Alert drop</span><strong>{profile.alertDropPct}%</strong></div><div><span>Hotel</span><strong>{profile.hotelEnabled ? profile.hotelCity || profile.destination : "Off"}</strong></div></div>
              <div className="card-actions">
                <button type="button" onClick={() => void onRun(profile.id)} disabled={busy || !profile.active}>Check now</button>
                <button type="button" onClick={() => onEdit(profile)}>Edit</button>
                <button className="danger-link" type="button" onClick={() => void onRemove(profile.id)}>Remove</button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function HankView({ state, dashboard, busy, onSend }: { state: HankState; dashboard: DashboardData | null; busy: boolean; onSend: (message: string) => Promise<string> }) {
  const [message, setMessage] = useState("");
  const [localMessages, setLocalMessages] = useState<{ role: "user" | "assistant"; content: string }[]>([]);
  const chatRef = useRef<HTMLDivElement>(null);
  const messages = useMemo(() => {
    const saved = dashboard?.chat.map((item) => ({ role: item.role, content: item.content })) ?? [];
    return [...saved, ...localMessages];
  }, [dashboard?.chat, localMessages]);

  useEffect(() => {
    const chat = chatRef.current;
    if (!chat) return;
    chat.scrollTo({ top: chat.scrollHeight, behavior: "smooth" });
  }, [messages.length, busy]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const clean = message.trim();
    if (!clean || busy) return;
    setMessage("");
    setLocalMessages((items) => [...items, { role: "user", content: clean }]);
    try {
      await onSend(clean);
      setLocalMessages([]);
    } catch (reason) {
      setLocalMessages((items) => [...items, { role: "assistant", content: `I hit a problem: ${errorMessage(reason)}` }]);
    }
  };

  return (
    <div className="page hank-page">
      <div className="hank-profile"><div className="hank-avatar">H</div><div><span className="eyebrow">Your travel agent</span><h1>Hank</h1><p><span className={`live-dot ${state.status}`} />{state.status === "idle" ? "Online and watching" : state.status}</p></div></div>
      <div className="chat-window" ref={chatRef}>
        {messages.length === 0 && <div className="hank-intro"><strong>What should I do?</strong><p>I can set up or edit watches, run searches, explain deals, and pause monitoring. I will never book or charge anything.</p><div className="prompt-chips"><button type="button" onClick={() => setMessage("Set up a new travel watch for me")}>Set up a watch</button><button type="button" onClick={() => setMessage("Check all my active watches now")}>Check everything</button><button type="button" onClick={() => setMessage("What is my best deal right now?")}>Best deal</button><button type="button" onClick={() => setMessage("What needs attention?")}>System status</button></div></div>}
        {messages.map((item, index) => <div className={`bubble ${item.role}`} key={`${item.role}-${index}`}><span>{item.role === "assistant" ? "Hank" : "You"}</span><p>{item.content}</p></div>)}
        {busy && <div className="bubble assistant thinking"><span>Hank</span><p><i /><i /><i /></p></div>}
      </div>
      <form className="chat-composer" onSubmit={submit}><input value={message} onChange={(event) => setMessage(event.target.value)} placeholder="Ask Hank or give a command…" maxLength={1500} autoComplete="off" enterKeyHint="send" /><button type="submit" aria-label="Send" disabled={!message.trim() || busy}>↑</button></form>
    </div>
  );
}

function SettingsView({ dashboard, onLogout }: { dashboard: DashboardData | null; onLogout: () => void }) {
  const status = dashboard?.providerStatus;
  return (
    <div className="page">
      <div className="page-title"><div><span className="eyebrow">Account & sources</span><h1>Settings</h1></div></div>
      <section className="settings-card"><h2>Connections</h2><Connection label="Workers AI" connected={status?.ai ?? false} detail="Hank reasoning and summaries" /><Connection label="LetsFG flights" connected={status?.flights ?? false} detail="Live flight fares" /><Connection label="LetsFG hotels" connected={status?.hotels ?? false} detail="Live refundable hotel rates" /><Connection label="Alert webhook" connected={status?.notifications ?? false} detail="External phone notifications" /></section>
      <section className="settings-card"><h2>Monitoring policy</h2><div className="policy-row"><span>Watch authority</span><strong className="safe">Create & edit</strong></div><div className="policy-row"><span>Schedule</span><strong>Every 6 hours</strong></div><div className="policy-row"><span>Purchase authority</span><strong className="safe">Disabled</strong></div><div className="policy-row"><span>Price validation</span><strong>Live recheck required</strong></div><p className="settings-note">Hank may create, edit, activate, pause, search, score, save, and notify. He cannot delete watches, book, enter passenger details, or spend money.</p></section>
      <section className="settings-card"><h2>Install on your phone</h2><p className="settings-note">In Safari or Chrome, open the share/menu button and choose <strong>Add to Home Screen</strong>. Hank then opens like a standalone app.</p></section>
      <button className="logout-button" type="button" onClick={onLogout}>Sign out of this device</button>
    </div>
  );
}

function ProfileSheet({ existing, onClose, onSave }: { existing: SearchProfile | null; onClose: () => void; onSave: (input: ProfileInput) => Promise<void> }) {
  const defaults = useMemo(() => defaultProfile(), []);
  const [form, setForm] = useState<ProfileInput>(existing ? profileToInput(existing) : defaults);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const patch = <K extends keyof ProfileInput>(key: K, value: ProfileInput[K]) => setForm((current) => ({ ...current, [key]: value }));
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setSaving(true); setError("");
    try { await onSave(form); } catch (reason) { setError(errorMessage(reason)); } finally { setSaving(false); }
  };
  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="profile-sheet" role="dialog" aria-modal="true" aria-label={existing ? "Edit travel watch" : "Create travel watch"}>
        <div className="sheet-handle" /><div className="sheet-title"><div><span className="eyebrow">Hank’s instructions</span><h2>{existing ? "Edit travel watch" : "New travel watch"}</h2></div><button type="button" onClick={onClose} aria-label="Close">×</button></div>
        <form onSubmit={submit}>
          <Field label="Watch name"><input required minLength={2} maxLength={60} value={form.name} onChange={(event) => patch("name", event.target.value)} placeholder="Tokyo in spring" /></Field>
          <div className="form-grid two"><Field label="From"><input required maxLength={3} value={form.origin} onChange={(event) => patch("origin", event.target.value.toUpperCase())} placeholder="YVR" /></Field><Field label="To"><input required maxLength={3} value={form.destination} onChange={(event) => patch("destination", event.target.value.toUpperCase())} placeholder="TYO" /></Field></div>
          <div className="form-grid two"><Field label="Depart from"><input required type="date" value={form.departureStart} onChange={(event) => patch("departureStart", event.target.value)} /></Field><Field label="Depart by"><input required type="date" value={form.departureEnd} onChange={(event) => patch("departureEnd", event.target.value)} /></Field></div>
          <div className="form-grid three"><Field label="Nights"><input type="number" min={1} max={60} value={form.tripLengthDays} onChange={(event) => patch("tripLengthDays", Number(event.target.value))} /></Field><Field label="Adults"><input type="number" min={1} max={9} value={form.adults} onChange={(event) => patch("adults", Number(event.target.value))} /></Field><Field label="Max stops"><select value={form.maxStops} onChange={(event) => patch("maxStops", Number(event.target.value))}><option value={0}>0</option><option value={1}>1</option><option value={2}>2</option><option value={3}>3</option></select></Field></div>
          <div className="form-grid two"><Field label="Cabin"><select value={form.cabin} onChange={(event) => patch("cabin", event.target.value as ProfileInput["cabin"])}><option value="M">Economy</option><option value="W">Premium economy</option><option value="C">Business</option><option value="F">First</option></select></Field><Field label="Currency"><input maxLength={3} value={form.currency} onChange={(event) => patch("currency", event.target.value.toUpperCase())} /></Field></div>
          <div className="form-grid two"><Field label="Trip target"><input type="number" min={1} value={form.targetTotal ?? ""} onChange={(event) => patch("targetTotal", event.target.value ? Number(event.target.value) : null)} placeholder="Optional" /></Field><Field label="Alert drop %"><input type="number" min={1} max={80} value={form.alertDropPct} onChange={(event) => patch("alertDropPct", Number(event.target.value))} /></Field></div>
          <label className="check-row"><input type="checkbox" checked={form.hotelEnabled} onChange={(event) => patch("hotelEnabled", event.target.checked)} /><span><strong>Include hotel total</strong><small>Refundable, pay-later inventory when the hotel source is connected.</small></span></label>
          {form.hotelEnabled && <><div className="form-grid two"><Field label="Hotel city"><input value={form.hotelCity} onChange={(event) => patch("hotelCity", event.target.value)} placeholder="Defaults to destination" /></Field><Field label="Guest country"><input maxLength={2} value={form.hotelNationality} onChange={(event) => patch("hotelNationality", event.target.value.toUpperCase())} /></Field></div><Field label="Max nightly hotel"><input type="number" min={1} value={form.hotelMaxNightly ?? ""} onChange={(event) => patch("hotelMaxNightly", event.target.value ? Number(event.target.value) : null)} placeholder="Optional" /></Field></>}
          {error && <p className="form-error">{error}</p>}
          <button className="primary-button sheet-submit" disabled={saving}>{saving ? "Saving…" : existing ? "Save changes" : "Start watching"}</button>
        </form>
      </section>
    </div>
  );
}

function AuthScreen({
  configured,
  passwordSalt,
  passwordIterations,
  onAuthChange,
}: {
  configured: boolean;
  passwordSalt: string | null;
  passwordIterations: number;
  onAuthChange: (status: AuthStatus) => void;
}) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const submittedPassword = String(new FormData(event.currentTarget).get("password") ?? password);
    if (submittedPassword.length < 12 || submittedPassword.length > 128) {
      setError("Use a passphrase between 12 and 128 characters.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      const salt = configured ? passwordSalt : randomBase64(18);
      if (!salt) throw new Error("The password verifier is unavailable. Refresh and try again.");
      const proof = await derivePasswordProof(submittedPassword, salt, passwordIterations);
      await fetchJson(configured ? "/api/auth/login" : "/api/auth/setup", {
        method: "POST",
        body: JSON.stringify(configured ? { proof } : { proof, salt, iterations: passwordIterations }),
        headers: { "Content-Type": "application/json" },
      });
      const status = await fetchJson<AuthStatus>("/api/auth/status");
      onAuthChange(status);
      if (!status.authenticated) {
        setError("Your passphrase was accepted, but this browser still needs you to sign in.");
      }
    } catch (reason) {
      const message = errorMessage(reason);
      if (!configured && message.includes("already been completed")) {
        try {
          const latest = await fetchJson<AuthStatus>("/api/auth/status");
          if (latest.configured && latest.passwordSalt && latest.passwordIterations) {
            const loginProof = await derivePasswordProof(
              submittedPassword,
              latest.passwordSalt,
              latest.passwordIterations,
            );
            await fetchJson("/api/auth/login", {
              method: "POST",
              body: JSON.stringify({ proof: loginProof }),
              headers: { "Content-Type": "application/json" },
            });
            const signedIn = await fetchJson<AuthStatus>("/api/auth/status");
            onAuthChange(signedIn);
            if (!signedIn.authenticated) {
              setError("Setup is complete. Enter the same passphrase again to sign in.");
            }
          } else {
            onAuthChange(latest);
          }
        } catch (loginReason) {
          const loginMessage = errorMessage(loginReason);
          setError(
            loginMessage === "Incorrect passphrase."
              ? "That is not the passphrase used when this app was first claimed."
              : loginMessage,
          );
        }
      } else {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  return <div className="auth-screen"><div className="auth-glow" /><section className="auth-card"><div className="auth-logo">H</div><span className="eyebrow">Hank Travel</span><h1>{configured ? "Welcome back" : "Claim your private app"}</h1><p>{configured ? "Enter your owner passphrase to open the command center." : "Create a passphrase of at least 12 characters. The first person to complete this step becomes the owner."}</p><form onSubmit={submit}><input autoFocus required name="password" type="password" autoComplete={configured ? "current-password" : "new-password"} minLength={12} maxLength={128} value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Owner passphrase" /><button className="primary-button" disabled={busy}>{busy ? "Securing…" : configured ? "Open Hank" : "Secure this app"}</button></form>{error && <p className="form-error">{error}</p>}<small>Use 12–128 characters. Your passphrase never leaves this device in plain text.</small></section></div>;
}

function DealCard({ deal }: { deal: DashboardData["deals"][number] }) { return <article className="deal-card"><div className="score-ring" style={{ "--score": `${deal.dealScore * 3.6}deg` } as React.CSSProperties}><span>{Math.round(deal.dealScore)}</span></div><div className="deal-main"><span className="eyebrow">{deal.profileName}</span><h2>{deal.origin}<span>→</span>{deal.destination}</h2><p>{deal.airline ?? "Multiple airlines"} · {deal.stops === 0 ? "Nonstop" : `${deal.stops ?? "?"} stop${deal.stops === 1 ? "" : "s"}`} · {deal.departureAt ? formatDate(deal.departureAt) : "Flexible"}</p><div className="deal-tags">{deal.isNewLow && <span>New low</span>}{deal.dropPct !== null && deal.dropPct > 0 && <span>{deal.dropPct.toFixed(0)}% below baseline</span>}{deal.hotelPrice !== null && <span>Flight + hotel</span>}</div></div><div className="deal-price"><strong>{money(deal.totalPrice, deal.currency)}</strong><span>{deal.hotelPrice === null ? "flight total" : "trip total"}</span></div></article>; }
function Stat({ label, value, accent = false }: { label: string; value: ReactNode; accent?: boolean }) { return <div className={`stat-card ${accent ? "accent" : ""}`}><strong>{value}</strong><span>{label}</span></div>; }
function SectionHeading({ title, action, onAction }: { title: string; action?: string; onAction?: () => void }) { return <div className="section-heading"><h2>{title}</h2>{action && <button type="button" onClick={onAction}>{action}</button>}</div>; }
function EmptyState({ title, body, action, onAction }: { title: string; body: string; action: string; onAction: () => void }) { return <section className="empty-state"><div className="empty-orbit"><span>✦</span></div><h2>{title}</h2><p>{body}</p><button className="ghost-button" type="button" onClick={onAction}>{action}</button></section>; }
function Connection({ label, connected, detail }: { label: string; connected: boolean; detail: string }) { return <div className="connection-row"><span className={`connection-icon ${connected ? "connected" : ""}`}>{connected ? "✓" : "!"}</span><div><strong>{label}</strong><span>{detail}</span></div><em>{connected ? "Connected" : "Needs setup"}</em></div>; }
function Field({ label, children }: { label: string; children: ReactNode }) { return <label className="field"><span>{label}</span>{children}</label>; }
function BottomNav({ tab, onChange }: { tab: Tab; onChange: (tab: Tab) => void }) { const items: { id: Tab; label: string; icon: string }[] = [{ id: "home", label: "Brief", icon: "⌂" }, { id: "watches", label: "Watches", icon: "◎" }, { id: "hank", label: "Hank", icon: "H" }, { id: "settings", label: "Settings", icon: "⚙" }]; return <nav className="bottom-nav">{items.map((item) => <button className={tab === item.id ? "active" : ""} type="button" key={item.id} onClick={() => onChange(item.id)}><i>{item.icon}</i><span>{item.label}</span></button>)}</nav>; }
function Splash() { return <div className="splash"><div className="auth-logo">H</div><span>Hank is arriving</span></div>; }
function CenteredMessage({ title, body }: { title: string; body: string }) { return <div className="auth-screen"><section className="auth-card"><div className="auth-logo">H</div><h1>{title}</h1><p>{body}</p></section></div>; }
function DashboardSkeleton() { return <div className="skeleton-stack"><div /><div /><div /></div>; }

function defaultProfile(): ProfileInput { const start = dateOffset(60); return { name: "", origin: "YVR", destination: "", departureStart: start, departureEnd: dateOffset(74), tripLengthDays: 7, adults: 1, cabin: "M", maxStops: 1, currency: "CAD", targetTotal: null, alertDropPct: 15, hotelEnabled: true, hotelCity: "", hotelNationality: "CA", hotelMaxNightly: null }; }
function profileToInput(profile: SearchProfile): ProfileInput { const { id: _id, active: _active, createdAt: _created, updatedAt: _updated, ...input } = profile; return input; }
function dateOffset(days: number): string { const date = new Date(); date.setUTCDate(date.getUTCDate() + days); return date.toISOString().slice(0, 10); }
function formatDate(value: string): string { const date = new Date(value.length === 10 ? `${value}T12:00:00Z` : value); return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", timeZone: "UTC" }).format(date); }
function formatRelative(value: string): string { const delta = Date.now() - Date.parse(value.endsWith("Z") ? value : `${value}Z`); const minutes = Math.max(0, Math.floor(delta / 60_000)); if (minutes < 1) return "now"; if (minutes < 60) return `${minutes}m`; const hours = Math.floor(minutes / 60); if (hours < 24) return `${hours}h`; return `${Math.floor(hours / 24)}d`; }
function money(value: number, currency: string): string { return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 0 }).format(value); }
async function fetchJson<T = unknown>(url: string, init?: RequestInit): Promise<T> { const response = await fetch(url, { credentials: "same-origin", ...init }); const raw = await response.text(); let body: unknown = null; try { body = raw ? JSON.parse(raw) : null; } catch { body = null; } if (!response.ok) { const message = typeof body === "object" && body !== null && typeof Reflect.get(body, "error") === "string" ? String(Reflect.get(body, "error")) : `Request failed (${response.status})`; throw new Error(message); } return body as T; }

async function derivePasswordProof(password: string, saltBase64: string, iterations: number): Promise<string> { const key = await crypto.subtle.importKey("raw", new TextEncoder().encode(password), "PBKDF2", false, ["deriveBits"]); const decodedSalt = fromBase64(saltBase64); const salt = new Uint8Array(decodedSalt.byteLength); salt.set(decodedSalt); const bits = await crypto.subtle.deriveBits({ name: "PBKDF2", hash: "SHA-256", salt, iterations }, key, 256); return toBase64(new Uint8Array(bits)); }
function randomBase64(length: number): string { const bytes = new Uint8Array(length); crypto.getRandomValues(bytes); return toBase64(bytes); }
function toBase64(bytes: Uint8Array): string { return btoa(String.fromCharCode(...bytes)); }
function fromBase64(value: string): Uint8Array { return Uint8Array.from(atob(value), (character) => character.charCodeAt(0)); }
function errorMessage(error: unknown): string { return error instanceof Error ? error.message : String(error); }

if ("serviceWorker" in navigator) window.addEventListener("load", () => { void navigator.serviceWorker.register("/sw.js"); });

createRoot(document.getElementById("root")!).render(<StrictMode><AppErrorBoundary><RootApp /></AppErrorBoundary></StrictMode>);
